import React, { useState, useEffect } from "react";
import "./webReport.css";
import Title from "../../components/Title";
import SubTitle from "../../components/SubTitle";
import Item from "../../components/Item";
import ConcerningLevelBadge from "../../components/ConcerningLevelBadge";
import FsLightbox from "fslightbox-react";
import { ConcerningLevelLibrary } from "app/containers/WebReportV1/helperFunctions";

const ItemSection = ({ issueItems }) => {
  /* Light box effects: https://fslightbox.com/react/documentation/updating-props */
  const [issueLightboxOpen, setIssueLightboxOpen] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentImages, setCurrentImages] = useState([]);
  const [lightboxToggler, setLightboxToggler] = useState(false);
  const [key, setKey] = useState(0); // Add a key counter

  // Add this useEffect
  useEffect(() => {
    if (issueLightboxOpen) {
      setLightboxToggler(true);
    }
  }, [key, issueLightboxOpen, currentImageIndex]);

  const toggleLightBox = (issueId, imageIndex, images) => {
    // Reset states first
    setLightboxToggler(false);

    // Set new states
    setCurrentImages(images);
    setCurrentImageIndex(imageIndex);
    setIssueLightboxOpen(issueId);
    setKey((prevKey) => prevKey + 1); // Increment key on each click
  };

  const issues = issueItems.map((issue) => {
    const hasSubsections = issue.subsections && issue.subsections.length > 0;

    return (
      <div className="individual-issue-wrapper" key={issue?.name}>
        <Title
          text={issue?.name}
          hasSubsections={hasSubsections}
          // issueTypeRecord={issueTypeRecord}
          // totalItems={totalItems}
          placedAt="itemSection"
        />
        {issue.overviews && issue.overviews.length > 0 && (
          <>
            {issue.overviews.map((overview, overviewIndex) => (
              <div key={overview.id} className="mb-3">
                <div className="mb-3">{overview.description}</div>
                {overview.images && overview.images.length > 0 && (
                  <div className="issue-content-row">
                    <div className="issue-images-grid">
                      {overview.images.map((image, index) => (
                        <div key={image.id} className="image-container">
                          <img
                            src={image.presigned_url}
                            alt={`Issue ${issue?.name} - Overview ${overviewIndex + 1} - ${image?.order}`}
                            className="issue-image"
                            onClick={() =>
                              toggleLightBox(
                                overview.description,
                                index,
                                overview.images.map((img) => img.presigned_url)
                              )
                            }
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </>
        )}
        {issue?.subsections.map((subsection) => {
          const totalItems =
            (subsection?.info_count || 0) +
            (subsection?.issue_count || 0) +
            (subsection?.limit_count || 0);

          // Map and create new objects instead of modifying existing ones
          const infos = (subsection?.infos || []).map((info) => ({
            ...info,
            type: "infos",
          }));

          const issues = (subsection?.issues || []).map((issue) => ({
            ...issue,
            type: "issues",
          }));

          const limits = (subsection?.limits || []).map((limit) => ({
            ...limit,
            type: "limits",
          }));

          const items = [...issues, ...infos, ...limits];

          return (
            <div key={subsection?.name} className="subsection-container">
              <SubTitle
                text={subsection?.name}
                // issueTypeRecord={issueTypeRecord}
                totalItems={totalItems}
                placedAt="itemSection"
              />
              {items.map((item, index) => {
                const itemContent = (
                  <div className="issue-content-wrapper">
                    <div className="issue-content-row">
                      <span className="label issue-field-label">
                        Description
                      </span>
                      <span className="field-content issue-field-content">
                        {item.description}
                      </span>
                    </div>
                    {item.cause && (
                      <div className="issue-content-row">
                        <span className="label issue-field-label">Cause</span>
                        <span className="field-content issue-field-content">
                          {item.cause}
                        </span>
                      </div>
                    )}
                    {item.solution && (
                      <div className="issue-content-row">
                        <span className="label issue-field-label">
                          Solution
                        </span>
                        <span className="field-content issue-field-content">
                          {item.solution}
                        </span>
                      </div>
                    )}
                    {item["code_selected"] ? (
                      <div className="issue-content-row">
                        <span className="label issue-field-label">Code</span>
                        <span className="field-content issue-field-content">
                          <span style={{ display: "inline-block" }}>
                            {issue["code_selected"].code}{" "}
                            {issue["code_selected"].section}
                          </span>
                          <span style={{ display: "inline-block" }}>
                            {issue["code_selected"].content}
                          </span>
                        </span>
                      </div>
                    ) : null}
                    {/* Add the images section */}
                    {item.images && item.images.length > 0 && (
                      <div className="issue-content-row">
                        <div className="issue-images-grid">
                          {item.images.map((image, index) => (
                            <div key={image.id} className="image-container">
                              <img
                                src={image.presigned_url}
                                alt={`Item ${item.description} - ${image.order}`}
                                className="issue-image"
                                onClick={() =>
                                  toggleLightBox(
                                    item.id,
                                    index,
                                    item.images.map((img) => img.presigned_url)
                                  )
                                }
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
                return (
                  <div
                    key={item.name || item.id}
                    id={item.id}
                    className="issue-container"
                  >
                    <Item
                      text={item.name || item.id}
                      children={itemContent}
                      showBadge
                      badgeContent={
                        <ConcerningLevelBadge
                          concerningLevel={item.concern_level || 0}
                          itemType={item.type}
                        />
                      }
                      placedAt="itemSection"
                      icon={
                        ConcerningLevelLibrary[item.type][
                          item.concern_level || 0
                        ]?.icon
                      }
                      showDivider={index !== items.length - 1}
                    />
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  });

  return (
    <div className="issue-section-container">
      {issues}
      <FsLightbox
        toggler={lightboxToggler}
        sources={currentImages}
        sourceIndex={currentImageIndex}
        type="image"
        key={key} // Use the counter as key to ensure the lightbox updates correctly
      />
    </div>
  );
};

export default ItemSection;
